import axios from "axios";

/** Normalized API failure for logging, toasts, and boundaries (serializable). */
export type UnifiedApiError = {
  readonly status: number;
  readonly message: string;
  readonly details?: unknown;
  readonly code?: string;
};

export class AppHttpError extends Error implements UnifiedApiError {
  readonly status: number;
  readonly details?: unknown;
  readonly code?: string;

  constructor(status: number, message: string, details?: unknown, code?: string) {
    super(message);
    this.name = "AppHttpError";
    this.status = status;
    this.details = details;
    this.code = code;
  }

  toJSON(): UnifiedApiError {
    return {
      status: this.status,
      message: this.message,
      details: this.details,
      code: this.code,
    };
  }
}

function messageFromResponseData(data: unknown): string | undefined {
  if (!data || typeof data !== "object") {
    return undefined;
  }
  if ("detail" in data) {
    const detail = (data as { detail: unknown }).detail;
    if (typeof detail === "string") {
      return detail;
    }
    if (Array.isArray(detail)) {
      return JSON.stringify(detail);
    }
  }
  if ("message" in data && typeof (data as { message: unknown }).message === "string") {
    return (data as { message: string }).message;
  }
  return undefined;
}

export function normalizeAxiosError(error: unknown): AppHttpError {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status ?? 0;
    const data = error.response?.data;
    const message =
      messageFromResponseData(data) ??
      error.message ??
      "Request failed";
    return new AppHttpError(status, message, data);
  }
  if (error instanceof AppHttpError) {
    return error;
  }
  if (error instanceof Error) {
    return new AppHttpError(0, error.message);
  }
  return new AppHttpError(0, "Unknown error");
}

export function toUnifiedApiError(error: unknown): UnifiedApiError {
  if (error instanceof AppHttpError) {
    return error.toJSON();
  }
  if (axios.isAxiosError(error)) {
    const e = normalizeAxiosError(error);
    return e.toJSON();
  }
  if (error instanceof Error) {
    return { status: 0, message: error.message };
  }
  return { status: 0, message: "Unknown error" };
}
